/**
 * Учёт фрагмента `#cmpimport=<value>` для финального редиректа из survey-client.
 * Хранится последнее увиденное значение; при сборке URL существующий hash целиком заменяется.
 *
 * @module utils/cmpimportHash
 */

const CMPIMPORT_KEY = 'cmpimport';
const CMPIMPORT_QUERY_KEYS = ['cmp_import', 'cmpimport'];

/** Последнее непустое значение cmpimport (сессия вкладки) */
let latestCmpimportValue: string | null = null;

/**
 * Декодирует значение cmpimport из hash/query строки.
 *
 * @param {string} rawValue - сырое URL-encoded значение
 * @returns {string | null} декодированное значение или null
 */
function decodeCmpimportValue(rawValue: string): string | null {
  if (rawValue === '') {
    return null;
  }

  try {
    const decoded = decodeURIComponent(rawValue.replaceAll('+', ' '));
    return decoded === '' ? null : decoded;
  } catch {
    return null;
  }
}

/**
 * Сохраняет новое значение cmpimport в памяти вкладки.
 *
 * @param {string | null} value - новое значение cmpimport
 * @returns {void}
 */
export function ingestCmpimportValue(value: string | null): void {
  if (value === null || value === '') {
    return;
  }

  latestCmpimportValue = value;
}

/**
 * Извлекает значение cmpimport из строки hash (`#...` или без ведущего `#`).
 *
 * @param {string} hash — фрагмент location.hash или аналог
 * @returns {string | null} декодированное значение или null
 */
export function parseCmpimportFromHash(hash: string): string | null {
  if (hash === '') {
    return null;
  }

  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
  if (trimmed === '') {
    return null;
  }

  const parts = trimmed.split('&');
  for (const part of parts) {
    if (part === '') {
      continue;
    }

    const eq = part.indexOf('=');
    if (eq <= 0) {
      continue;
    }

    const key = part.slice(0, eq);
    if (key !== CMPIMPORT_KEY) {
      continue;
    }

    return decodeCmpimportValue(part.slice(eq + 1));
  }

  return null;
}

/**
 * Извлекает значение cmpimport из query-строки (`?cmp_import=...` или `?cmpimport=...`).
 *
 * @param {string} search - строка поиска URL
 * @returns {string | null} найденное значение или null
 */
export function parseCmpimportFromSearch(search: string): string | null {
  if (search === '') {
    return null;
  }

  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(normalized);

  for (const key of CMPIMPORT_QUERY_KEYS) {
    const rawValue = params.get(key);
    if (typeof rawValue !== 'string') {
      continue;
    }

    const decoded = decodeCmpimportValue(rawValue);
    if (decoded !== null) {
      return decoded;
    }
  }

  return null;
}

/**
 * Обновляет сохранённое значение cmpimport из hash текущей страницы (если ключ присутствует).
 *
 * @param {string} hash — обычно `window.location.hash`
 * @returns {void}
 */
export function ingestCmpimportFromWindowHash(hash: string): void {
  ingestCmpimportValue(parseCmpimportFromHash(hash));
}

/**
 * Обновляет сохранённое значение cmpimport из query текущей страницы.
 *
 * @param {string} search - обычно `window.location.search`
 * @returns {void}
 */
export function ingestCmpimportFromWindowSearch(search: string): void {
  ingestCmpimportValue(parseCmpimportFromSearch(search));
}

/**
 * Возвращает последнее зафиксированное значение cmpimport.
 *
 * @returns {string | null}
 */
export function getLatestCmpimportValue(): string | null {
  return latestCmpimportValue;
}

/**
 * Подставляет в URL hash вида `#cmpimport=<encoded>`; если сохранённого значения нет — URL без изменений.
 *
 * @param {string} targetUrl — абсолютный или относительный URL
 * @returns {string} полный URL строкой
 */
export function applyCmpimportToRedirectUrl(targetUrl: string): string {
  if (latestCmpimportValue === null || latestCmpimportValue === '') {
    return targetUrl;
  }

  const base =
    typeof document !== 'undefined' && document.baseURI !== undefined && document.baseURI !== ''
      ? document.baseURI
      : undefined;
  const link = new URL(targetUrl, base);
  link.hash = `${CMPIMPORT_KEY}=${encodeURIComponent(latestCmpimportValue)}`;
  return link.toString();
}
